import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowLeftRight, FileText, AlertTriangle, CheckCircle,
  ChevronDown, ChevronRight, Scale, Copy, Loader2, X, FileUp, Clipboard,
  Target, Download, RefreshCw, Check, ThumbsUp, ThumbsDown, Minus, Award,
  AlertCircle, Shield, Zap, Eye, MessageSquare, TrendingUp, TrendingDown, Clock
} from 'lucide-react';

const PROTECTION_LEVEL_CONFIG = {
  bajo: { color: 'red', bgClass: 'bg-red-500/20', textClass: 'text-red-400', gradient: 'from-red-600 to-red-700' },
  moderado: { color: 'yellow', bgClass: 'bg-yellow-500/20', textClass: 'text-yellow-400', gradient: 'from-yellow-500 to-yellow-600' },
  alto: { color: 'emerald', bgClass: 'bg-emerald-500/20', textClass: 'text-emerald-400', gradient: 'from-emerald-500 to-emerald-600' },
  muy_alto: { color: 'blue', bgClass: 'bg-blue-500/20', textClass: 'text-blue-400', gradient: 'from-blue-500 to-blue-600' }
};

const CONTRACT_TYPES = [
  { id: "arriendo", label: "Arriendo" },
  { id: "compraventa", label: "Compraventa" },
  { id: "laboral", label: "Laboral" },
  { id: "servicios", label: "Prestación de Servicios" },
  { id: "general", label: "Otro / General" }
];

const USER_ROLES = {
  arriendo: [
    { id: "arrendador", label: "Arrendador (Dueño)" },
    { id: "arrendatario", label: "Arrendatario (Inquilino)" }
  ],
  compraventa: [
    { id: "vendedor", label: "Vendedor" },
    { id: "comprador", label: "Comprador" }
  ],
  laboral: [
    { id: "empleador", label: "Empleador" },
    { id: "trabajador", label: "Trabajador" }
  ],
  servicios: [
    { id: "prestador", label: "Prestador de Servicios" },
    { id: "mandante", label: "Mandante / Cliente" }
  ],
  general: [
    { id: "parte_a", label: "Primera Parte" },
    { id: "parte_b", label: "Segunda Parte" }
  ]
};

const ContractComparisonPage = () => {
  const { api } = useAuth();
  
  // Input state
  const [contractA, setContractA] = useState('');
  const [contractB, setContractB] = useState('');
  const [labelA, setLabelA] = useState('Original');
  const [labelB, setLabelB] = useState('Propuesto');
  const [contractType, setContractType] = useState('');
  const [userRole, setUserRole] = useState('');
  
  // Comparison state
  const [comparing, setComparing] = useState(false);
  const [comparison, setComparison] = useState(null);
  
  // UI state
  const [expandedDiff, setExpandedDiff] = useState(null);
  const [expandedNegotiation, setExpandedNegotiation] = useState(null);
  const [activeTab, setActiveTab] = useState('differences'); // differences, analysis, recommendation

  const handlePasteA = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setContractA(text);
      toast.success('Texto pegado en Contrato A');
    } catch (error) {
      toast.error('No se pudo acceder al portapapeles');
    }
  }, []);

  const handlePasteB = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setContractB(text);
      toast.success('Texto pegado en Contrato B');
    } catch (error) {
      toast.error('No se pudo acceder al portapapeles');
    }
  }, []);

  const handleFileUploadA = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setContractA(text);
      toast.success(`Archivo cargado en ${labelA}`);
    } catch (error) {
      toast.error('Error al leer el archivo');
    }
  }, [labelA]);

  const handleFileUploadB = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      setContractB(text);
      toast.success(`Archivo cargado en ${labelB}`);
    } catch (error) {
      toast.error('Error al leer el archivo');
    }
  }, [labelB]);

  const compareContracts = async () => {
    if (!contractA.trim() || !contractB.trim()) {
      toast.error('Por favor ingrese ambos contratos');
      return;
    }

    if (contractA.length < 100 || contractB.length < 100) {
      toast.error('Ambos contratos deben tener al menos 100 caracteres');
      return;
    }

    setComparing(true);
    setComparison(null);

    try {
      const response = await api.post('/contract-protection/compare', {
        contract_a: contractA,
        contract_b: contractB,
        contract_type: contractType || 'general',
        user_role: userRole || null,
        label_a: labelA,
        label_b: labelB
      });

      setComparison(response.data);
      toast.success('Comparación completada');
    } catch (error) {
      console.error('Comparison error:', error);
      toast.error(error.response?.data?.detail || 'Error al comparar contratos');
    } finally {
      setComparing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  const resetAll = () => {
    setContractA('');
    setContractB('');
    setComparison(null);
    setContractType('');
    setUserRole('');
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-emerald-400';
    if (score >= 50) return 'text-yellow-400';
    if (score >= 25) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 75) return 'bg-emerald-500/20';
    if (score >= 50) return 'bg-yellow-500/20';
    if (score >= 25) return 'bg-orange-500/20';
    return 'bg-red-500/20';
  };

  const getFavorsIcon = (favors, labelA, labelB) => {
    if (favors === labelA) return <TrendingUp className="w-4 h-4 text-blue-400" />;
    if (favors === labelB) return <TrendingDown className="w-4 h-4 text-purple-400" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  return (
    <div className="min-h-screen mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 text-slate-400 hover:text-white transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <div className="flex items-center space-x-3">
              <ArrowLeftRight className="w-6 h-6 text-purple-500" />
              <span className="font-semibold font-['Outfit']">Comparar Contratos</span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/historial-proteccion"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-amber-400 hover:text-amber-300 transition-colors"
                data-testid="history-link"
              >
                <Clock className="w-4 h-4" />
                <span className="hidden sm:inline">Historial</span>
              </Link>
              <Link
                to="/proteger-contrato"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
              >
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Proteger</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {!comparison ? (
          /* Input Phase */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold font-['Outfit'] mb-3">
                Compara Dos Versiones de Contrato
              </h1>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Sube o pega dos versiones de un contrato. El sistema identificará diferencias 
                significativas y evaluará cuál te ofrece mejor protección.
              </p>
            </div>

            {/* Contract Inputs Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Contract A */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-blue-400" />
                  </div>
                  <input
                    type="text"
                    value={labelA}
                    onChange={(e) => setLabelA(e.target.value)}
                    className="bg-transparent border-b border-slate-700 focus:border-blue-500 outline-none font-semibold"
                    placeholder="Nombre del contrato"
                  />
                </div>
                <div className="relative">
                  <textarea
                    value={contractA}
                    onChange={(e) => setContractA(e.target.value)}
                    placeholder="Pega aquí el primer contrato (ej: versión original)..."
                    className="w-full h-64 p-4 bg-slate-800/50 border border-blue-500/30 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:border-blue-500 font-mono text-sm"
                    data-testid="contract-a-input"
                  />
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    <button
                      onClick={handlePasteA}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1"
                    >
                      <Clipboard className="w-3 h-3" /> Pegar
                    </button>
                    <label className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1 cursor-pointer">
                      <FileUp className="w-3 h-3" /> Subir
                      <input type="file" accept=".txt" onChange={handleFileUploadA} className="hidden" />
                    </label>
                    {contractA && (
                      <button onClick={() => setContractA('')} className="px-2 py-1.5 bg-slate-700 hover:bg-red-600/50 rounded-lg">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{contractA.length.toLocaleString()} caracteres</p>
              </div>

              {/* Contract B */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-purple-400" />
                  </div>
                  <input
                    type="text"
                    value={labelB}
                    onChange={(e) => setLabelB(e.target.value)}
                    className="bg-transparent border-b border-slate-700 focus:border-purple-500 outline-none font-semibold"
                    placeholder="Nombre del contrato"
                  />
                </div>
                <div className="relative">
                  <textarea
                    value={contractB}
                    onChange={(e) => setContractB(e.target.value)}
                    placeholder="Pega aquí el segundo contrato (ej: versión propuesta)..."
                    className="w-full h-64 p-4 bg-slate-800/50 border border-purple-500/30 rounded-xl text-white placeholder-slate-500 resize-none focus:outline-none focus:border-purple-500 font-mono text-sm"
                    data-testid="contract-b-input"
                  />
                  <div className="absolute bottom-3 right-3 flex gap-2">
                    <button
                      onClick={handlePasteB}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1"
                    >
                      <Clipboard className="w-3 h-3" /> Pegar
                    </button>
                    <label className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs flex items-center gap-1 cursor-pointer">
                      <FileUp className="w-3 h-3" /> Subir
                      <input type="file" accept=".txt" onChange={handleFileUploadB} className="hidden" />
                    </label>
                    {contractB && (
                      <button onClick={() => setContractB('')} className="px-2 py-1.5 bg-slate-700 hover:bg-red-600/50 rounded-lg">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500">{contractB.length.toLocaleString()} caracteres</p>
              </div>
            </div>

            {/* Options */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-slate-400 block mb-2">Tipo de Contrato</label>
                  <select
                    value={contractType}
                    onChange={(e) => { setContractType(e.target.value); setUserRole(''); }}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    data-testid="contract-type-select"
                  >
                    <option value="">Detectar automáticamente</option>
                    {CONTRACT_TYPES.map(ct => (
                      <option key={ct.id} value={ct.id}>{ct.label}</option>
                    ))}
                  </select>
                </div>
                
                {contractType && USER_ROLES[contractType] && (
                  <div>
                    <label className="text-sm text-slate-400 block mb-2">Tu Rol</label>
                    <select
                      value={userRole}
                      onChange={(e) => setUserRole(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-emerald-500"
                    >
                      <option value="">Seleccionar rol</option>
                      {USER_ROLES[contractType].map(role => (
                        <option key={role.id} value={role.id}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-end">
                  <button
                    onClick={compareContracts}
                    disabled={comparing || !contractA.trim() || !contractB.trim()}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="compare-contracts-btn"
                  >
                    {comparing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Comparando...
                      </>
                    ) : (
                      <>
                        <ArrowLeftRight className="w-5 h-5" />
                        Comparar Contratos
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Info Card */}
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-purple-300">
                <Zap className="w-5 h-5" />
                ¿Qué analiza el comparador?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Diferencias en cláusulas clave (plazos, pagos, garantías)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Nivel de protección de cada versión</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Cláusulas que favorecen a cada parte</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>Puntos de negociación sugeridos</span>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          /* Results Phase */
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Recommendation Banner */}
            {comparison.recommendation && (
              <div className={`mb-6 p-6 rounded-xl bg-gradient-to-r ${
                comparison.recommendation.preferred_contract === labelA 
                  ? 'from-blue-600/30 to-blue-800/30 border border-blue-500/50' 
                  : 'from-purple-600/30 to-purple-800/30 border border-purple-500/50'
              }`}>
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                      <Award className="w-6 h-6 text-amber-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white mb-1">
                        Recomendación: {comparison.recommendation.preferred_contract}
                      </h2>
                      <p className="text-slate-300 text-sm max-w-2xl">
                        {comparison.recommendation.reasoning?.slice(0, 200)}...
                      </p>
                      <span className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                        comparison.recommendation.confidence === 'alta' ? 'bg-emerald-500/20 text-emerald-400' :
                        comparison.recommendation.confidence === 'media' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-orange-500/20 text-orange-400'
                      }`}>
                        Confianza: {comparison.recommendation.confidence}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Score Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Contract A Score */}
              <div className={`p-5 rounded-xl border ${
                comparison.recommendation?.preferred_contract === labelA 
                  ? 'border-emerald-500 bg-emerald-500/10' 
                  : 'border-slate-700 bg-slate-800/50'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-blue-400" />
                    </div>
                    <span className="font-semibold">{comparison.labels?.a || labelA}</span>
                  </div>
                  {comparison.recommendation?.preferred_contract === labelA && (
                    <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center gap-1">
                      <ThumbsUp className="w-3 h-3" /> Recomendado
                    </span>
                  )}
                </div>
                <div className="text-center py-4">
                  <div className={`text-5xl font-bold ${getScoreColor(comparison.protection_analysis?.contract_a?.score || 0)}`}>
                    {comparison.protection_analysis?.contract_a?.score || 0}
                  </div>
                  <div className="text-sm text-slate-400 mt-1">Puntuación de Protección</div>
                  <div className={`mt-2 text-xs px-2 py-1 rounded inline-block ${
                    PROTECTION_LEVEL_CONFIG[comparison.protection_analysis?.contract_a?.level]?.bgClass || 'bg-slate-700'
                  } ${PROTECTION_LEVEL_CONFIG[comparison.protection_analysis?.contract_a?.level]?.textClass || 'text-slate-400'}`}>
                    {comparison.protection_analysis?.contract_a?.level?.toUpperCase()}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-emerald-400 text-xs font-medium">Fortalezas:</span>
                    <ul className="mt-1 space-y-1">
                      {comparison.protection_analysis?.contract_a?.strengths?.slice(0, 2).map((s, i) => (
                        <li key={i} className="text-slate-300 flex items-start gap-1">
                          <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Contract B Score */}
              <div className={`p-5 rounded-xl border ${
                comparison.recommendation?.preferred_contract === labelB 
                  ? 'border-emerald-500 bg-emerald-500/10' 
                  : 'border-slate-700 bg-slate-800/50'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-purple-400" />
                    </div>
                    <span className="font-semibold">{comparison.labels?.b || labelB}</span>
                  </div>
                  {comparison.recommendation?.preferred_contract === labelB && (
                    <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center gap-1">
                      <ThumbsUp className="w-3 h-3" /> Recomendado
                    </span>
                  )}
                </div>
                <div className="text-center py-4">
                  <div className={`text-5xl font-bold ${getScoreColor(comparison.protection_analysis?.contract_b?.score || 0)}`}>
                    {comparison.protection_analysis?.contract_b?.score || 0}
                  </div>
                  <div className="text-sm text-slate-400 mt-1">Puntuación de Protección</div>
                  <div className={`mt-2 text-xs px-2 py-1 rounded inline-block ${
                    PROTECTION_LEVEL_CONFIG[comparison.protection_analysis?.contract_b?.level]?.bgClass || 'bg-slate-700'
                  } ${PROTECTION_LEVEL_CONFIG[comparison.protection_analysis?.contract_b?.level]?.textClass || 'text-slate-400'}`}>
                    {comparison.protection_analysis?.contract_b?.level?.toUpperCase()}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-emerald-400 text-xs font-medium">Fortalezas:</span>
                    <ul className="mt-1 space-y-1">
                      {comparison.protection_analysis?.contract_b?.strengths?.slice(0, 2).map((s, i) => (
                        <li key={i} className="text-slate-300 flex items-start gap-1">
                          <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 flex-shrink-0" />
                          <span className="text-xs">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 border-b border-slate-700 pb-2">
              <button
                onClick={() => setActiveTab('differences')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  activeTab === 'differences' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Diferencias ({comparison.differences?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('negotiation')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  activeTab === 'negotiation' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Puntos de Negociación ({comparison.negotiation_points?.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('warnings')}
                className={`px-4 py-2 rounded-t-lg transition-colors ${
                  activeTab === 'warnings' ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                Advertencias
              </button>
            </div>

            {/* Differences Tab */}
            {activeTab === 'differences' && (
              <div className="space-y-3">
                {comparison.differences?.map((diff, idx) => {
                  const isExpanded = expandedDiff === idx;
                  return (
                    <div key={idx} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedDiff(isExpanded ? null : idx)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            diff.significance === 'alta' ? 'bg-red-500/20' :
                            diff.significance === 'media' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                          }`}>
                            {getFavorsIcon(diff.favors, comparison.labels?.a, comparison.labels?.b)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                diff.significance === 'alta' ? 'bg-red-500/20 text-red-400' :
                                diff.significance === 'media' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                {diff.significance}
                              </span>
                              <span className="text-xs px-2 py-0.5 rounded bg-slate-700 text-slate-300">
                                {diff.aspect}
                              </span>
                              {diff.favors !== 'neutral' && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  diff.favors === comparison.labels?.a ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                                }`}>
                                  Favorece: {diff.favors}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-slate-300">{diff.analysis}</p>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-slate-700"
                          >
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                                <span className="text-xs text-blue-400 block mb-2">{comparison.labels?.a}:</span>
                                <p className="text-sm text-blue-200">{diff.contract_a_text || "No especificado"}</p>
                              </div>
                              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                <span className="text-xs text-purple-400 block mb-2">{comparison.labels?.b}:</span>
                                <p className="text-sm text-purple-200">{diff.contract_b_text || "No especificado"}</p>
                              </div>
                              {diff.impact_on_user && (
                                <div className="md:col-span-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                  <span className="text-xs text-amber-400 block mb-1">Impacto para ti:</span>
                                  <p className="text-sm text-amber-200">{diff.impact_on_user}</p>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Negotiation Points Tab */}
            {activeTab === 'negotiation' && (
              <div className="space-y-3">
                {comparison.negotiation_points?.map((point, idx) => {
                  const isExpanded = expandedNegotiation === idx;
                  return (
                    <div key={idx} className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => setExpandedNegotiation(isExpanded ? null : idx)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            point.priority === 'alta' ? 'bg-red-500/20' :
                            point.priority === 'media' ? 'bg-yellow-500/20' : 'bg-blue-500/20'
                          }`}>
                            <MessageSquare className={`w-4 h-4 ${
                              point.priority === 'alta' ? 'text-red-400' :
                              point.priority === 'media' ? 'text-yellow-400' : 'text-blue-400'
                            }`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                point.priority === 'alta' ? 'bg-red-500/20 text-red-400' :
                                point.priority === 'media' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-blue-500/20 text-blue-400'
                              }`}>
                                Prioridad {point.priority}
                              </span>
                            </div>
                            <h4 className="font-medium">{point.point}</h4>
                          </div>
                          <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="border-t border-slate-700"
                          >
                            <div className="p-4 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                  <span className="text-xs text-red-400 block mb-1">Estado Actual:</span>
                                  <p className="text-sm text-red-200">{point.current_state}</p>
                                </div>
                                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                                  <span className="text-xs text-emerald-400 block mb-1">Estado Deseado:</span>
                                  <p className="text-sm text-emerald-200">{point.target_state}</p>
                                </div>
                              </div>
                              {point.negotiation_tip && (
                                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-2">
                                  <Scale className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <span className="text-xs text-amber-400 block mb-1">Consejo de Negociación:</span>
                                    <p className="text-sm text-amber-200">{point.negotiation_tip}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
                {(!comparison.negotiation_points || comparison.negotiation_points.length === 0) && (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay puntos de negociación identificados</p>
                  </div>
                )}
              </div>
            )}

            {/* Warnings Tab */}
            {activeTab === 'warnings' && (
              <div className="space-y-4">
                {comparison.recommendation?.warnings?.map((warning, idx) => (
                  <div key={idx} className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-200">{warning}</p>
                  </div>
                ))}
                {comparison.recommendation?.conditions && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-300 mb-1">Condiciones Antes de Firmar:</h4>
                        <p className="text-amber-200">{comparison.recommendation.conditions}</p>
                      </div>
                    </div>
                  </div>
                )}
                {comparison.legal_notes && (
                  <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Scale className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-slate-300 mb-1">Notas Legales:</h4>
                        <p className="text-slate-400">{comparison.legal_notes}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={resetAll}
                className="px-6 py-3 border border-slate-700 rounded-xl hover:bg-slate-800 flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Nueva Comparación
              </button>
              <Link
                to="/proteger-contrato"
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center gap-2"
              >
                <Shield className="w-4 h-4" />
                Proteger un Contrato
              </Link>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default ContractComparisonPage;
